import React, { useState, useEffect } from 'react';
import { Card, Typography, Spin, message, Tag, Space, Button, Image, Row, Col, Grid, Alert, Modal, Form, Input, DatePicker, Select, Upload } from 'antd';
import { EyeOutlined, DownloadOutlined, FilePdfOutlined, FileImageOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import { authService } from '../services/authService';
import { masterDataService, Employee, Department, ChucDanh, ChucVu, ChuyenMon } from '../services/masterDataService';
import { userProfileService, GiayToDto, LoaiGiayToDto, NhanVienAnhDaiDienDto, CreateGiayToRequest, UpdateGiayToRequest } from '../services/hoSoNhanVienService';
import dayjs from 'dayjs';
import type { UploadFile } from 'antd';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const UserProfile: React.FC = () => {
  const [userProfile, setUserProfile] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Hồ sơ nhân viên
  const [employeeDetail, setEmployeeDetail] = useState<Employee | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [chucDanhList, setChucDanhList] = useState<ChucDanh[]>([]);
  const [chucVuList, setChucVuList] = useState<ChucVu[]>([]);
  const [chuyenMonList, setChuyenMonList] = useState<ChuyenMon[]>([]);
  
  // Ảnh đại diện
  const [anhDaiDien, setAnhDaiDien] = useState<NhanVienAnhDaiDienDto | null>(null);
  const [anhDaiDienBlobUrl, setAnhDaiDienBlobUrl] = useState<string | null>(null);
  
  // Giấy tờ
  const [giayToList, setGiayToList] = useState<GiayToDto[]>([]);
  const [loaiGiayToList, setLoaiGiayToList] = useState<LoaiGiayToDto[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewItem, setPreviewItem] = useState<GiayToDto | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  
  // Modal tạo/sửa giấy tờ
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGiayTo, setEditingGiayTo] = useState<GiayToDto | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const profile = await authService.getProfile();
        setUserProfile(profile);
        
        if (profile.macc) {
          // Fetch thông tin nhân viên chi tiết
          const empRes = await masterDataService.getEmployees(true);
          const employee = empRes.data.find((e: Employee) => e.macc === profile.macc);
          setEmployeeDetail(employee || null);
          
          // Fetch danh mục
          const [deptRes, chucDanhRes, chucVuRes, chuyenMonRes] = await Promise.all([
            masterDataService.getDepartments(),
            masterDataService.getChucDanh(),
            masterDataService.getChucVu(),
            masterDataService.getChuyenMon(),
          ]);
          setDepartments(deptRes.data);
          setChucDanhList(chucDanhRes.data);
          setChucVuList(chucVuRes.data);
          setChuyenMonList(chuyenMonRes.data);
          
          // Fetch ảnh đại diện
          try {
            const anhRes = await userProfileService.getAnhDaiDien(profile.macc);
            setAnhDaiDien(anhRes.data);
            if (anhRes.data) {
              const blob = await userProfileService.getAnhDaiDienPreviewBlob(profile.macc);
              setAnhDaiDienBlobUrl(URL.createObjectURL(blob));
            }
          } catch {
            // Ảnh đại diện không tồn tại
          }
          
          // Fetch giấy tờ
          try {
            const [giayToRes, loaiGiayToRes] = await Promise.all([
              userProfileService.getGiayToListIncludingPending(profile.macc),
              userProfileService.getLoaiGiayToList(),
            ]);
            setGiayToList(giayToRes.data);
            setLoaiGiayToList(loaiGiayToRes.data);
          } catch {
            message.error('Không tải được danh sách giấy tờ');
          }
        }
      } catch (err: any) {
        setError(err.response?.data || 'Failed to load profile');
        message.error('Failed to load profile.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handlePreview = async (record: GiayToDto) => {
    setPreviewItem(record);
    setPreviewVisible(true);
    setPreviewBlobUrl(null);
    try {
      const blob = await userProfileService.getPreviewBlob(record.id);
      setPreviewBlobUrl(URL.createObjectURL(blob));
    } catch {
      message.error('Không tải được file để xem');
    }
  };

  const closePreview = () => {
    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    setPreviewBlobUrl(null);
    setPreviewVisible(false);
    setPreviewItem(null);
  };

  const handleDownload = async (record: GiayToDto) => {
    try {
      const blob = await userProfileService.getDownloadBlob(record.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = record.tenFileGoc;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      message.error('Không tải được file');
    }
  };

  const handleDownloadAnhDaiDien = () => {
    if (!anhDaiDien || !userProfile) return;
    Modal.confirm({
      title: 'Tải ảnh đại diện',
      content: 'Bạn có chắc chắn muốn tải ảnh đại diện này không?',
      okText: 'Tải',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          const blob = await userProfileService.getAnhDaiDienDownloadBlob(userProfile.macc);
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.target = '_blank';
          link.download = anhDaiDien.tenFileGoc;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } catch {
          message.error('Không tải được ảnh');
        }
      },
    });
  };

  const showCreateModal = () => {
    setEditingGiayTo(null);
    setFileList([]);
    form.resetFields();
    setModalVisible(true);
  };

  const showEditModal = (record: GiayToDto) => {
    setEditingGiayTo(record);
    setFileList([]);
    form.setFieldsValue({
      maLoaiGiayTo: record.maLoaiGiayTo,
      ten: record.ten,
      maSo: record.maSo,
      noiCap: record.noiCap,
      ngayCap: record.ngayCap ? dayjs(record.ngayCap) : null,
      ngayHetHan: record.ngayHetHan ? dayjs(record.ngayHetHan) : null,
      ghiChu: record.ghiChu,
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setModalLoading(true);

      if (editingGiayTo) {
        // Update
        const updateData: UpdateGiayToRequest = {
          maLoaiGiayTo: values.maLoaiGiayTo,
          ten: values.ten,
          maSo: values.maSo,
          noiCap: values.noiCap,
          ngayCap: values.ngayCap ? values.ngayCap.format('YYYY-MM-DD') : undefined,
          ngayHetHan: values.ngayHetHan ? values.ngayHetHan.format('YYYY-MM-DD') : undefined,
          ghiChu: values.ghiChu,
        };
        await userProfileService.updateGiayTo(editingGiayTo.id, updateData);
        message.success('Cập nhật giấy tờ thành công');
      } else {
        // Create
        if (fileList.length === 0) {
          message.error('Vui lòng chọn file');
          setModalLoading(false);
          return;
        }
        const file = fileList[0].originFileObj as File;
        if (!file) {
          message.error('File không hợp lệ');
          setModalLoading(false);
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          message.error('Dung lượng file vượt quá 10 MB');
          setModalLoading(false);
          return;
        }
        if (!file.name.toLowerCase().endsWith('.pdf')) {
          message.error('Chỉ chấp nhận file PDF');
          setModalLoading(false);
          return;
        }

        const createData: CreateGiayToRequest = {
          maChamCong: userProfile.macc,
          maLoaiGiayTo: values.maLoaiGiayTo,
          ten: values.ten,
          maSo: values.maSo,
          noiCap: values.noiCap,
          ngayCap: values.ngayCap ? values.ngayCap.format('YYYY-MM-DD') : undefined,
          ngayHetHan: values.ngayHetHan ? values.ngayHetHan.format('YYYY-MM-DD') : undefined,
          ghiChu: values.ghiChu,
        };
        await userProfileService.createGiayTo(createData, file);
        message.success('Giấy tờ đã được tạo và đang chờ duyệt');
      }

      setModalVisible(false);
      form.resetFields();
      setFileList([]);
      
      // Reload giấy tờ
      if (userProfile.macc) {
        const giayToRes = await userProfileService.getGiayToList(userProfile.macc);
        setGiayToList(giayToRes.data);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Thao tác thất bại');
    } finally {
      setModalLoading(false);
    }
  };

  const beforeUpload = (file: UploadFile) => {
    const isPdf = file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      message.error('Chỉ chấp nhận file PDF');
      return Upload.LIST_IGNORE;
    }
    const isLt10M = (file.size || 0) / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Dung lượng file phải nhỏ hơn 10MB');
      return Upload.LIST_IGNORE;
    }
    // Return false để prevent auto upload, nhưng vẫn cho file hiển thị trong list
    return false;
  };

  const handleFileChange = (info: any) => {
    const { fileList } = info;
    // Giữ lại file nhưng prevent auto upload
    setFileList(fileList.slice(-1));
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <Spin size="large" tip="Đang tải..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px', color: 'red' }}>
        <Title level={4}>Lỗi: {error}</Title>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <Title level={4}>Không tìm thấy thông tin hồ sơ</Title>
      </div>
    );
  }

  const getExpiryStatus = (ngayHetHan: string | null) => {
    if (!ngayHetHan) return { color: 'blue', text: 'Vĩnh viễn' };
    const exp = dayjs(ngayHetHan);
    const now = dayjs();
    const diffDays = exp.diff(now, 'day');
    if (diffDays < 0) return { color: 'red', text: 'Đã hết hạn' };
    if (diffDays <= 30) return { color: 'orange', text: `Còn ${diffDays} ngày` };
    if (diffDays <= 90) return { color: 'gold', text: `Còn ${diffDays} ngày` };
    return { color: 'green', text: `Còn ${diffDays} ngày` };
  };

  const getFileIcon = (loaiFile: string) => {
    const ext = loaiFile.toLowerCase();
    if (ext === 'pdf') return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
    return <FileImageOutlined style={{ color: '#1890ff' }} />;
  };

  const getDepartmentName = (makk: number | null) => {
    return departments.find(d => d.makk === makk)?.tenkk || '-';
  };

  // Sắp xếp giấy tờ theo thứ tự hiển thị của loại giấy tờ
  const sortedGiayToList = [...giayToList].sort((a, b) => {
    const loaiA = loaiGiayToList.find(l => l.maLoaiGiayTo === a.maLoaiGiayTo);
    const loaiB = loaiGiayToList.find(l => l.maLoaiGiayTo === b.maLoaiGiayTo);
    const thuTuA = loaiA?.thuTuHienThi ?? 999;
    const thuTuB = loaiB?.thuTuHienThi ?? 999;
    return thuTuA - thuTuB;
  });

  // Responsive columns cho giấy tờ
  const getGiayToColumns = () => {
    if (screens.xl) return 4;
    if (screens.lg) return 3;
    if (screens.md) return 2;
    return 1;
  };

  return (
    <div style={{ padding: isMobile ? '0' : '24px' }}>
      <Card 
        title={<Title level={4}>Hồ sơ nhân viên</Title>} 
        extra={<Text type="secondary">{userProfile.employeeName || userProfile.macc}</Text>}
      >
        {/* Ảnh đại diện */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <div 
            style={{ 
              width: 200, 
              height: 250, 
              borderRadius: 8, 
              overflow: 'hidden', 
              border: '1px solid #d9d9d9', 
              background: '#fff', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              cursor: anhDaiDien ? 'pointer' : 'not-allowed',
              opacity: anhDaiDien ? 1 : 0.5
            }}
            onClick={anhDaiDien ? handleDownloadAnhDaiDien : undefined}
          >
            {anhDaiDienBlobUrl ? (
              <Image 
                src={anhDaiDienBlobUrl} 
                alt="Ảnh đại diện" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                preview={false}
              />
            ) : (
              <span style={{ color: '#bbb', fontSize: 14 }}>Chưa có ảnh</span>
            )}
          </div>
        </div>

        {/* Thông tin cá nhân */}
        <Card size="small" title="Thông tin cá nhân" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Mã NV:</Text> {employeeDetail?.macc || '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Họ tên:</Text> {employeeDetail?.tennv || '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Ngày sinh:</Text> {employeeDetail?.ngaysinh ? dayjs(employeeDetail.ngaysinh).format('DD/MM/YYYY') : '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Giới tính:</Text> {employeeDetail?.maphai === 1 ? 'Nam' : employeeDetail?.maphai === 2 ? 'Nữ' : '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>CMND/CCCD:</Text> {employeeDetail?.chungminhthu || '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Nơi cấp:</Text> {employeeDetail?.noicapcmt || '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Ngày cấp:</Text> {employeeDetail?.ngaycapcmt ? dayjs(employeeDetail.ngaycapcmt).format('DD/MM/YYYY') : '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Email:</Text> {employeeDetail?.email || '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Điện thoại:</Text> {employeeDetail?.dienthoai || '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Khoa:</Text> {employeeDetail ? getDepartmentName(employeeDetail.makk) : '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Trình độ chuyên môn:</Text> {employeeDetail ? chucDanhList.find(c => c.machucdanh === employeeDetail.machucdanh)?.tenchucdanh : '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Chức vụ:</Text> {employeeDetail ? chucVuList.find(c => c.machucvu === employeeDetail.machucvu)?.tenchucvu : '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Chuyên môn:</Text> {employeeDetail ? chuyenMonList.find(c => c.machuyenmon === employeeDetail.machuyenmon)?.tenchuyenmon : '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Chứng chỉ nghề:</Text> {employeeDetail?.chungchinghe || '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Ngày cấp CCHN:</Text> {employeeDetail?.ngaycapcc ? dayjs(employeeDetail.ngaycapcc).format('DD/MM/YYYY') : '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Mã BHXH:</Text> {employeeDetail?.mabhxh || '-'}
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text strong>Tình trạng:</Text> {employeeDetail?.nghiviec ? <Tag color="red">Đã nghỉ việc</Tag> : <Tag color="green">Đang làm việc</Tag>}
            </Col>
          </Row>
        </Card>

        {/* Danh sách giấy tờ */}
        <Card size="small" title="Giấy tờ" extra={<Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>Thêm giấy tờ</Button>}>
          {sortedGiayToList.length === 0 ? (
            <Alert message="Không có giấy tờ" type="info" />
          ) : (
            <Row gutter={[16, 16]}>
              {sortedGiayToList.map((item) => {
                const loaiGiayTo = loaiGiayToList.find(l => l.maLoaiGiayTo === item.maLoaiGiayTo);
                const status = getExpiryStatus(item.ngayHetHan);
                const isPending = !item.active;
                return (
                  <Col xs={24} sm={12} md={8} lg={6} xl={6} key={item.id}>
                    <Card
                      size="small"
                      hoverable
                      style={{
                        height: '100%',
                        background: isPending ? '#fffbe6' : (status.color === 'red' ? '#fff1f0' : 'white'),
                        border: `1px solid ${isPending ? '#ffe58f' : (status.color === 'red' ? '#ffccc7' : '#f0f0f0')}`,
                      }}
                    >
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 14 }}>
                          {loaiGiayTo?.tenLoai || '-'}
                        </div>
                        {item.ten && (
                          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{item.ten}</div>
                        )}
                      </div>
                      
                      <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
                        <div><Text type="secondary">Số:</Text> {item.maSo || '-'}</div>
                        <div><Text type="secondary">Nơi cấp:</Text> {item.noiCap || '-'}</div>
                      </div>
                      
                      <div style={{ marginBottom: 12 }}>
                        {isPending ? (
                          <Tag color="orange" style={{ marginBottom: 8 }}>Chờ duyệt</Tag>
                        ) : (
                          <Tag color={status.color} style={{ marginBottom: 8 }}>{status.text}</Tag>
                        )}
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {getFileIcon(item.loaiFile)}
                        <Space>
                          <Button 
                            type="link" 
                            size="small" 
                            icon={<EyeOutlined />} 
                            onClick={() => handlePreview(item)}
                          >
                            Xem
                          </Button>
                          <Button 
                            type="link" 
                            size="small" 
                            icon={<DownloadOutlined />} 
                            onClick={() => handleDownload(item)}
                          >
                            Tải
                          </Button>
                          <Button 
                            type="link" 
                            size="small" 
                            icon={<EditOutlined />} 
                            onClick={() => showEditModal(item)}
                          >
                            Sửa
                          </Button>
                        </Space>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Card>
      </Card>
      
      {/* Modal Preview */}
      <Modal
        title={previewItem?.tenLoai}
        open={previewVisible}
        onCancel={closePreview}
        footer={
          previewItem ? (
            <Button icon={<DownloadOutlined />} onClick={() => previewItem && handleDownload(previewItem)}>
              Tải về
            </Button>
          ) : null
        }
        width={800}
      >
        {previewItem && (
          <div style={{ textAlign: 'center' }}>
            {!previewBlobUrl ? (
              <div style={{ padding: 40, color: '#999' }}>Đang tải file...</div>
            ) : ['jpg', 'jpeg', 'png'].includes(previewItem.loaiFile.toLowerCase()) ? (
              <Image
                src={previewBlobUrl}
                alt={previewItem.tenFileGoc}
                style={{ maxWidth: '100%', maxHeight: '70vh' }}
              />
            ) : (
              <iframe
                src={previewBlobUrl}
                title={previewItem.tenFileGoc}
                style={{ width: '100%', height: '70vh', border: 'none' }}
              />
            )}
            <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
              File: {previewItem.tenFileGoc}
            </div>
          </div>
        )}
      </Modal>

      {/* Modal Tạo/Sửa Giấy Tờ */}
      <Modal
        title={editingGiayTo ? 'Sửa giấy tờ' : 'Thêm giấy tờ mới'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setFileList([]);
        }}
        confirmLoading={modalLoading}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="maLoaiGiayTo"
            label="Loại giấy tờ"
            rules={[{ required: true, message: 'Vui lòng chọn loại giấy tờ' }]}
          >
            <Select placeholder="Chọn loại giấy tờ">
              {loaiGiayToList.filter(l => l.active).map(loai => (
                <Select.Option key={loai.maLoaiGiayTo} value={loai.maLoaiGiayTo}>
                  {loai.tenLoai}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="ten"
            label="Tên giấy tờ"
          >
            <Input placeholder="Nhập tên giấy tờ" />
          </Form.Item>

          <Form.Item
            name="maSo"
            label="Số giấy tờ"
          >
            <Input placeholder="Nhập số giấy tờ" />
          </Form.Item>

          <Form.Item
            name="noiCap"
            label="Nơi cấp"
          >
            <Input placeholder="Nhập nơi cấp" />
          </Form.Item>

          <Form.Item
            name="ngayCap"
            label="Ngày cấp"
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item
            name="ngayHetHan"
            label="Ngày hết hạn"
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item
            name="ghiChu"
            label="Ghi chú"
          >
            <Input.TextArea rows={3} placeholder="Nhập ghi chú" />
          </Form.Item>

          {!editingGiayTo && (
            <div>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>File giấy tờ <span style={{ color: 'red' }}>*</span></div>
              <Upload
                fileList={fileList}
                beforeUpload={beforeUpload}
                onChange={handleFileChange}
                onRemove={() => setFileList([])}
                maxCount={1}
                accept=".pdf"
                showUploadList={true}
              >
                <Button icon={<PlusOutlined />}>Chọn file PDF</Button>
              </Upload>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                Chỉ chấp nhận file PDF, tối đa 10MB
              </div>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default UserProfile;
